// pages/BlogDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { blogAPI, utils } from './api'; // Fixed import path

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching blog with slug:', slug);
        
        const blogResponse = await blogAPI.getBlogBySlug(slug);
        console.log('Blog API response:', blogResponse);
        
        // Handle different response structures
        const blogData = blogResponse.data || blogResponse.blog || blogResponse;
        
        if (!blogData) {
          throw new Error('Blog data not found in response');
        }

        // Process blog data
        const processedBlog = {
          ...blogData,
          read_time: utils.calculateReadTime(blogData.content),
          excerpt: utils.generateExcerpt(blogData.meta_description || blogData.content || blogData.title)
        };

        console.log('Processed blog:', processedBlog);
        setBlog(processedBlog);

        // Fetch related blogs
        try {
          const related = await blogAPI.getRelatedBlogs(slug, processedBlog.department);
          console.log('Related blogs:', related);
          setRelatedBlogs(Array.isArray(related) ? related : []);
        } catch (relatedError) {
          console.warn('Failed to fetch related blogs:', relatedError);
          setRelatedBlogs([]);
        }

      } catch (err) {
        const errorMessage = err.message || 'Blog not found or failed to load.';
        setError(errorMessage);
        console.error('Error fetching blog:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogData();
    } else {
      setError('No blog slug provided');
      setLoading(false);
    }
  }, [slug]);

  const getDepartment = () => {
    if (!blog) return 'Healthcare';
    
    // Try to get department from blog data first
    if (blog.department || blog.category) {
      return blog.department || blog.category;
    }
    
    // Fallback to extracting from title
    const words = blog.title.split(' ');
    return words.length > 2 ? `${words[0]} ${words[1]}` : 'Healthcare';
  };

  if (loading) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate('/resources/blogs')}>← Back to Blogs</BackButton>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading blog post...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (error || !blog) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate('/resources/blogs')}>← Back to Blogs</BackButton>
        <ErrorMessage>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{error || 'Blog not found'}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/resources/blogs')}>← Back to Blogs</BackButton>
      
      <BlogHeader>
        <BlogImage 
          src={blog.featured_image_url || blog.image_url || blog.thumbnail} 
          alt={blog.title}
        />
        <HeaderOverlay />
        <HeaderContent>
          {/* <DepartmentBadge>{getDepartment()}</DepartmentBadge> */}
          <BlogTitle>{blog.title}</BlogTitle>
          <BlogMeta>
            <AuthorInfo>
              <AuthorDetails>
                <AuthorName>{blog.author_name || 'Admin'}</AuthorName>
                <PublishInfo>
                  {utils.formatDate(blog.published_at || blog.created_at || blog.date)} • {blog.read_time} read
                </PublishInfo>
              </AuthorDetails>
            </AuthorInfo>
          </BlogMeta>
        </HeaderContent>
      </BlogHeader>

      <ContentWrapper>
        <MainContent>
          <BlogContent>
            {blog.content ? (
              <div 
                dangerouslySetInnerHTML={{ 
                  __html: blog.content 
                }} 
              />
            ) : blog.excerpt ? (
              <NoContent>
                <p>{blog.excerpt}</p>
                <p style={{ fontStyle: 'italic', color: '#718096', marginTop: '20px' }}>
                  Full content not available.
                </p>
              </NoContent>
            ) : (
              <NoContent>No content available for this blog.</NoContent>
            )}
          </BlogContent>
        </MainContent>

        <Sidebar>
          <RelatedBlogsSection>
            <SidebarTitle>Related Blogs</SidebarTitle>
            <RelatedBlogsList>
              {relatedBlogs.length > 0 ? (
                relatedBlogs.map(relatedBlog => (
                  <RelatedBlogCard 
                    key={relatedBlog.id} 
                    onClick={() => navigate(`/resources/blogs/${relatedBlog.slug}`)}
                  >
                    <RelatedBlogImage 
                      src={relatedBlog.featured_image_url} 
                      alt={relatedBlog.title}
                    />
                    <RelatedBlogContent>
                      <RelatedBlogTitle>
                        {relatedBlog.title}
                      </RelatedBlogTitle>
                      <RelatedBlogMeta>
                        {utils.formatDate(relatedBlog.published_at)}
                      </RelatedBlogMeta>
                    </RelatedBlogContent>
                  </RelatedBlogCard>
                ))
              ) : (
                <NoRelatedBlogs>
                  No related blogs found.
                </NoRelatedBlogs>
              )}
            </RelatedBlogsList>
          </RelatedBlogsSection>

          {/* Additional sidebar content */}
          <SidebarSection>
            <SidebarTitle>About Author</SidebarTitle>
            <AuthorSection>
              <AuthorAvatar>
                {blog.author_name ? blog.author_name.charAt(0).toUpperCase() : 'A'}
              </AuthorAvatar>
              <AuthorDetailsSidebar>
                <AuthorNameSidebar>{blog.author_name || 'Admin'}</AuthorNameSidebar>
                <AuthorBio>
                  Healthcare professional and medical writer with expertise in patient education.
                </AuthorBio>
              </AuthorDetailsSidebar>
            </AuthorSection>
          </SidebarSection>
        </Sidebar>
      </ContentWrapper>
    </PageContainer>
  );
};

// Enhanced Styled Components
const PageContainer = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: 'Inter', sans-serif;
  background-color: #f8fafc;
  min-height: 100vh;

  @media (max-width: 600px){
      padding: 5px 5px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4299e1;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.1rem;
  color: #718096;
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const ErrorIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
`;

const ErrorText = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
`;

const RetryButton = styled.button`
  background: #c53030;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 10px;

  &:hover {
    background: #a53030;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #4299e1;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 30px;
  padding: 12px 20px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #3182ce;
    background-color: #ebf8ff;
  }
`;

const BlogHeader = styled.div`
  position: relative;
  margin-bottom: 50px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);

    
  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 10px;
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
`;

const HeaderOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60%;
  background: linear-gradient(transparent, #004AAD);
`;

const HeaderContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 60px 40px 40px;
  color: white;
`;

const DepartmentBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 20px;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 20px 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-weight: 600;
  font-size: 1.1rem;
`;

const PublishInfo = styled.span`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 50px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const MainContent = styled.div``;

const BlogContent = styled.article`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  line-height: 1.8;
  color: #4a5568;

  /* Style HTML content from database */
  h1, h2, h3, h4, h5, h6 {
    color: #2d3748;
    margin: 24px 0 16px 0;
  }

  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }

  p {
    margin-bottom: 20px;
    font-size: 1.1rem;
  }

  ul, ol {
    margin: 16px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
  }

  blockquote {
    border-left: 4px solid #4299e1;
    padding-left: 20px;
    margin: 20px 0;
    font-style: italic;
    color: #718096;
    background: #f7fafc;
    padding: 20px;
    border-radius: 8px;
  }

  code {
    background: #edf2f7;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
  }

  pre {
    background: #2d3748;
    color: #e2e8f0;
    padding: 20px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 20px 0;
  }

  @media (max-width: 600px){
    padding: 5px;
    }
`;

const NoContent = styled.div`
  text-align: center;
  color: #718096;
  font-style: italic;
  padding: 40px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const RelatedBlogsSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 100px;

  @media (max-width: 600px){
    padding: 5px;
  }
`;

const SidebarSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    @media (max-width: 600px){
    padding: 5px;
  }
`;

const SidebarTitle = styled.h3`
  font-size: 1.5rem;
  color: #2d3748;
  margin: 0 0 25px 0;
  padding-bottom: 15px;
  border-bottom: 2px solid #e2e8f0;
  font-weight: 700;
`;

const RelatedBlogsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RelatedBlogCard = styled.div`
  display: flex;
  gap: 15px;
  padding: 15px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: 1px solid #e2e8f0;

  &:hover {
    background-color: #f7fafc;
    border-color: #cbd5e0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedBlogImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
`;

const RelatedBlogContent = styled.div`
  flex: 1;
`;

const RelatedBlogTitle = styled.h4`
  font-size: 0.9rem;
  color: #2d3748;
  margin: 0 0 8px 0;
  line-height: 1.4;
  font-weight: 600;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RelatedBlogMeta = styled.div`
  color: #718096;
  font-size: 0.75rem;
`;

const NoRelatedBlogs = styled.div`
  text-align: center;
  color: #718096;
  font-style: italic;
  padding: 20px;
`;

const AuthorSection = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-start;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
`;

const AuthorDetailsSidebar = styled.div`
  flex: 1;
`;

const AuthorNameSidebar = styled.div`
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 5px;
`;

const AuthorBio = styled.div`
  font-size: 0.9rem;
  color: #718096;
  line-height: 1.5;
`;

export default BlogDetailPage;